/**
 * 后端只有一个端点：POST /api/bazi，靠 action 字段分发（共 47 个）。
 * 已登录时会话对象并入 payload.userSession —— 这是旧站的约定，后端照此解析，不能改。
 *
 * 完整 action 契约见 spec/06-api-contract.md。
 */

const ENDPOINT = "/api/bazi";
const SESSION_KEY = "tengyunzi.session";

export type ApiResult<T = unknown> = ({ ok: true } & T) | { ok: false; code?: string; error: string };

export type Session = {
  token: string;
  ownerId?: string;
  isAdmin?: boolean;
  [key: string]: unknown;
};

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function setSession(session: Session | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* 隐私模式下 localStorage 可能不可用，忽略即可 */
  }
}

/**
 * 页面浏览打点（PV）。每次进入/切换页面时上报一条，用于后台「访问记录」和支付漏斗访客口径。
 * 不阻塞渲染、不关心返回：用 keepalive 让请求在页面切换时也能发出，任何错误都吞掉。
 * 登录态下会带上 userSession，让后端把 PV 归到具体用户；未登录则记为 anonymous。
 */
export function trackPageview(route: string): void {
  if (typeof window === "undefined") return;
  try {
    const session = getSession();
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        action: "access.track",
        payload: {
          path: route,
          ref: document.referrer || "",
          ...(session ? { userSession: session } : {}),
        },
      }),
    }).catch(() => {});
  } catch {
    /* 打点是尽力而为，失败不影响页面 */
  }
}

export async function callApi<T = Record<string, unknown>>(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<ApiResult<T>> {
  try {
    const session = getSession();
    const response = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
        payload: session ? { ...payload, userSession: session } : payload,
      }),
    });
    const body = (await response.json().catch(() => null)) as ApiResult<T> | null;
    if (response.ok && body) return body;
    return body ?? { ok: false, error: `API request failed: ${response.status}` };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}
